/**
 * Inbox API client (Inbox V2, spec §3.2 + §4.1).
 *
 * Wraps the additive gateway endpoints in apps/chaos-gateway/src/mentions.ts:
 *   GET  /api/orgs/:orgId/inbox?category=needs_you            → { items, nextCursor }
 *   GET  /api/orgs/:orgId/inbox?category=notification&cursor= → { items, nextCursor }
 *   GET  /api/orgs/:orgId/inbox/counts                        → { needsYou }
 *   POST /api/mentions/:id/dismiss                            → { mention }
 *
 * Marking seen / acted-on still routes through `mentionsApi` (same rows). The
 * read endpoints exist regardless of the gateway INBOX_V2_ENABLED flag (the flag
 * gates the producers), so a flag-ON UI against a flag-OFF gateway just sees
 * empty lanes — never an error.
 *
 * `useInboxEvents()` mirrors `useBoardEvents` byte-for-byte (reconnect with
 * exponential backoff, ping/hello ignore, reconnect-resync) but keyed on the
 * /api/inbox/events socket, which fans out only to the authed user. On every
 * data frame it invalidates the inbox query keys so the Needs-You badge / lists
 * update live; the 30s poll on the queries stays as a network-blip fallback.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { env } from '../../env';
import { apiJson } from './client';
import type { Mention } from './mentions';

// ── Read shapes ─────────────────────────────────────────────────────
export interface InboxPage {
  items: Mention[];
  nextCursor: string | null;
}
interface InboxCountsResponse {
  needsYou: number;
}
interface MentionResponse {
  mention: Mention;
}

/** The in-session ask payload the AskPopup renders (spec §1.4 / §3.3). */
export interface SessionAsk {
  id: string;
  /** Exact owning chat session; a card may have several historical sessions. */
  sessionId: string;
  cardId: string;
  orgId: string;
  question: string;
  /** Option labels; empty array = free-text only. */
  options: string[];
  allowFreeText: boolean;
  kind: string;
  status: 'open' | 'answered' | 'cancelled' | 'expired';
  createdAt: string;
  /** Durable ask-answer ledger. `deliveredAt` is the only proof of delivery. */
  answerText?: string | null;
  deliveredAt?: string | null;
  /**
   * WP-4a / D-14: may THIS viewer answer? Only the session's executor can
   * (the answer runs on their subscription). Sent only while the gateway's
   * actor/executor guard is on; absent → the dialog behaves as before.
   */
  answerableByViewer?: boolean;
  /** Whom to name in "only <name> can answer". Null when unknown. */
  executorDisplayName?: string | null;
}

export interface AskAnswerReceipt {
  ok: true;
  /** Backward-compatible shape for older AskPopup/clients. */
  queued?: true;
  reason?: string | null;
  /** Added with the delivery ledger; absent on an older gateway during rollout. */
  askId?: string;
  /** Added with the delivery ledger; normalize legacy 2xx receipts in the UI. */
  delivery?:
    | { state: 'delivered' }
    | { state: 'queued'; reason?: string | null }
    | { state: 'unknown' };
}

/**
 * `GET /api/cards/:cardId/open-ask` — the card's human-facing wait state.
 *
 * TWO DIFFERENT WAITS, ONE PAYLOAD (2026-07-27). Named "open-ask" for history;
 * it now answers both halves of "is this card waiting on me?", deliberately in
 * a single response so the board glyph, the card header and the session dock
 * cannot compute three different answers from three different reads:
 *
 *   open     — the agent is BLOCKED and cannot continue (§M). Blue raised hand.
 *   yourTurn — the agent FINISHED and the ball is in your court; nothing is
 *              stuck (§Y). Amber hourglass.
 *
 * Mutually exclusive by server construction, never both true.
 */
export interface OpenAskState {
  open: boolean;
  askId?: string;
  /** Answered but not yet delivered ask, durable across a card remount. */
  deliveryAskId?: string;
  /** True when its saved answer is owned by a live capacity queue row. */
  deliveryQueued?: boolean;
  /** Dispatch started, but the delivered_at stamp is not yet readable. */
  deliveryStarted?: boolean;
  /** Exact durable server refusal; the only remount-safe Retry authority. */
  deliveryRejected?: boolean;
  /** Absent on an older gateway ⇒ treated as false, board renders as before. */
  yourTurn?: boolean;
}

/** A role that holds the needed access (the Connect picker source). */
export interface AccessHolder {
  roleId: string;
  roleSlug: string;
  roleName: string;
  canWrite: boolean;
}

/** The access-request payload the AccessRequestPopup renders (migration 0104). */
export interface AccessRequest {
  id: string;
  orgId: string;
  cardId: string | null;
  sessionId: string;
  roleId: string | null;
  resourceKind: string;
  resourceName: string | null;
  needed: string;
  axis: 'read' | 'write';
  holders: AccessHolder[];
  status: 'open' | 'resolved' | 'cancelled';
  resolution: 'granted' | 'connected' | null;
  /** Permanent grant offered only to founder/admin; temporary otherwise. */
  canGrantPermanent: boolean;
  canGrantTemporary: boolean;
  createdAt: string;
}

export const inboxApi = {
  /** Needs You — OPEN items only, newest-first, capped (no cursor). */
  listNeedsYou(orgId: string): Promise<InboxPage> {
    return apiJson<InboxPage>(`/orgs/${orgId}/inbox?category=needs_you`);
  },
  /** Notifications — keyset-paginated; pass the previous page's nextCursor. */
  listNotifications(orgId: string, cursor?: string | null, limit = 40): Promise<InboxPage> {
    const qs = new URLSearchParams({ category: 'notification', limit: String(limit) });
    if (cursor) qs.set('cursor', cursor);
    return apiJson<InboxPage>(`/orgs/${orgId}/inbox?${qs.toString()}`);
  },
  /** Badge — the count of OPEN needs-you items only. */
  counts(orgId: string): Promise<number> {
    return apiJson<InboxCountsResponse>(`/orgs/${orgId}/inbox/counts`).then((r) => r.needsYou);
  },
  // ── GLOBAL (cross-org) variants (founder 2026-07-05) ─────────────────
  // The Inbox is the user's PERSONAL, GLOBAL view: Needs You + Notifications
  // across ALL their orgs, not just the active one. The gateway resolves the
  // org set + keeps the recipient fence (target = the acting user). Each item
  // carries its own `orgId` so the UI can badge which org it belongs to.
  /** Needs You across ALL the user's orgs (open items, newest-first, capped). */
  listNeedsYouGlobal(): Promise<InboxPage> {
    return apiJson<InboxPage>(`/inbox?category=needs_you`);
  },
  /** Notifications across ALL the user's orgs (keyset-paginated). */
  listNotificationsGlobal(cursor?: string | null, limit = 40): Promise<InboxPage> {
    const qs = new URLSearchParams({ category: 'notification', limit: String(limit) });
    if (cursor) qs.set('cursor', cursor);
    return apiJson<InboxPage>(`/inbox?${qs.toString()}`);
  },
  /** Global badge — OPEN needs-you count summed across the user's orgs. */
  countsGlobal(): Promise<number> {
    return apiJson<InboxCountsResponse>(`/inbox/counts`).then((r) => r.needsYou);
  },
  /** Dismiss a Notification row (clear without "acting"). */
  dismiss(id: string): Promise<Mention> {
    return apiJson<MentionResponse>(`/mentions/${id}/dismiss`, { method: 'POST' }).then(
      (r) => r.mention,
    );
  },
  /** Read one in-session ask (the AskPopup payload — question + options). */
  getAsk(askId: string): Promise<SessionAsk> {
    return apiJson<SessionAsk>(`/session-asks/${askId}`);
  },
  /**
   * Answer an in-session ask → wakes the session + injects the answer verbatim.
   * `answerText` is the clicked option label or the free-text the human typed.
   * The handler is first-writer-wins on the open→answered transition (409 if a
   * race lost), so the caller surfaces "already answered" on a 409.
   */
  answerAsk(
    askId: string,
    body: { answerText: string; retry?: boolean },
    principalFence: string,
  ): Promise<AskAnswerReceipt> {
    const suffix = body.retry ? '?retry=1' : '';
    return apiJson<AskAnswerReceipt>(`/session-asks/${askId}/answer${suffix}`, {
      method: 'POST',
      headers: { 'X-Chaos-Expected-Principal': principalFence },
      json: body,
    });
  },
  /** Board / card raised-hand badge: does this card have an OPEN ask? */
  getOpenAsk(cardId: string): Promise<OpenAskState> {
    return apiJson<OpenAskState>(`/cards/${cardId}/open-ask`);
  },

  // ── Access-request resolution (migration 0104) ───────────────────────
  /** Read one access-request (the AccessRequestPopup payload). */
  getAccessRequest(id: string): Promise<AccessRequest> {
    return apiJson<AccessRequest>(`/access-requests/${id}`);
  },
  /**
   * Grant the blocked role the needed access. mode='temporary' → a bounded,
   * read-only delegated grant (default 24h); mode='permanent' → a standing grant
   * (founder/admin only). A write denial cannot be granted temporarily (422).
   */
  grantAccessRequest(
    id: string,
    body: { mode: 'temporary' | 'permanent'; ttlSeconds?: number },
  ): Promise<{ ok: true; mode: string; grantId: string; ttlSeconds?: number }> {
    return apiJson(`/access-requests/${id}/grant`, { method: 'POST', json: body });
  },
  /** Connect a holder role into the blocked session (its grants apply next run). */
  connectAccessRequest(id: string, body: { roleId: string }): Promise<{ ok: true; connectedRoleId: string }> {
    return apiJson(`/access-requests/${id}/connect`, { method: 'POST', json: body });
  },
};

// ── Query keys ──────────────────────────────────────────────────────
export const inboxKeys = {
  all: ['inbox'] as const,
  needsYou: (orgId: string) => [...inboxKeys.all, 'needs-you', orgId] as const,
  notifications: (orgId: string) => [...inboxKeys.all, 'notifications', orgId] as const,
  counts: (orgId: string) => [...inboxKeys.all, 'counts', orgId] as const,
  // GLOBAL cross-org keys (no org id).
  needsYouGlobal: () => [...inboxKeys.all, 'needs-you', 'global'] as const,
  notificationsGlobal: () => [...inboxKeys.all, 'notifications', 'global'] as const,
  countsGlobal: () => [...inboxKeys.all, 'counts', 'global'] as const,
  ask: (askId: string) => [...inboxKeys.all, 'ask', askId] as const,
  openAsk: (cardId: string) => [...inboxKeys.all, 'open-ask', cardId] as const,
  accessRequest: (id: string) => [...inboxKeys.all, 'access-request', id] as const,
};

// ════════════════════════════════════════════════════════════════════
// Live inbox events — clone of useBoardEvents, keyed on the per-user
// /api/inbox/events socket.
// ════════════════════════════════════════════════════════════════════

interface PingFrame {
  event: 'ping';
}
interface HelloFrame {
  event: 'hello';
  userId: string;
}
export interface InboxDataFrame {
  op: 'INSERT' | 'UPDATE';
  userId: string | null;
  category: 'needs_you' | 'notification';
  mentionId: string;
  cardId: string | null;
}
type ServerFrame = PingFrame | HelloFrame | InboxDataFrame;

/**
 * Resolve the inbox-events WS URL. Mirrors `wsUrl` in use-board-events.ts: swap
 * http→ws / https→wss off the same-origin API base. The gateway registers the
 * route at /api/inbox/events (Caddy proxies /api/* to the gateway and passes the
 * WS upgrade through transparently).
 */
function inboxWsUrl(): string {
  const base = env.VITE_API_BASE_URL;
  let url: URL;
  try {
    url = new URL(base, window.location.origin);
  } catch {
    url = new URL(window.location.origin);
  }
  const proto = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${url.host}/api/inbox/events`;
}

export interface UseInboxEventsOptions {
  enabled?: boolean;
  /** The active org id whose inbox query keys to invalidate on each frame. */
  orgId?: string | null;
  /**
   * Fired ONLY on a fresh needs-you row (op:INSERT && category:needs_you) — the
   * shell wires this to a proactive toast + OS Notification. Never fires for
   * post-hoc notification-lane rows or the seen/acted UPDATE, so routine
   * activity can never ping. De-dup by mentionId is the caller's job.
   */
  onNeedsYou?: (frame: InboxDataFrame) => void;
  /** Override the WS URL — used by unit tests with mock servers. */
  url?: string;
  /** Custom WebSocket constructor — injected in tests. */
  WebSocketCtor?: typeof WebSocket;
}

export interface UseInboxEventsResult {
  reconnecting: boolean;
}

/** Idle window to collapse a burst of inbox frames into one invalidation pass. */
const INBOX_EVENT_DEBOUNCE_MS = 120;

export function useInboxEvents(opts: UseInboxEventsOptions = {}): UseInboxEventsResult {
  const qc = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUs = useRef(false);
  const attemptRef = useRef(0);
  const hasConnected = useRef(false);
  const [reconnecting, setReconnecting] = useState(false);
  const invalidateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const enabled = opts.enabled !== false;
  const orgId = opts.orgId ?? null;
  const overrideUrl = opts.url;
  const Ctor = opts.WebSocketCtor;

  // Keep the needs-you callback in a ref so a new closure identity on each
  // render does NOT tear down + re-open the socket (one WS per tab).
  const onNeedsYouRef = useRef(opts.onNeedsYou);
  onNeedsYouRef.current = opts.onNeedsYou;

  useEffect(() => {
    if (!enabled) return;

    // Invalidate the whole inbox tree so badge + both lanes refetch. Debounced
    // so an INSERT + the immediate seen/acted UPDATE collapse into one pass.
    const flush = (): void => {
      if (invalidateTimer.current) {
        clearTimeout(invalidateTimer.current);
        invalidateTimer.current = null;
      }
      void qc.invalidateQueries({ queryKey: inboxKeys.all });
    };
    const queueFlush = (): void => {
      if (invalidateTimer.current) return;
      invalidateTimer.current = setTimeout(flush, INBOX_EVENT_DEBOUNCE_MS);
    };

    const connect = (): void => {
      const Constructor = Ctor ?? WebSocket;
      const url = overrideUrl ?? inboxWsUrl();
      const ws = new Constructor(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        // Reconnect after a drop: refetch authoritative state once (we may have
        // missed frames while offline). First-ever open is the happy path.
        if (hasConnected.current) queueFlush();
        hasConnected.current = true;
        setReconnecting(false);
      };
      ws.onmessage = (msgEvent) => {
        const data =
          typeof msgEvent.data === 'string'
            ? msgEvent.data
            : new TextDecoder().decode(msgEvent.data as ArrayBuffer);
        let frame: ServerFrame;
        try {
          frame = JSON.parse(data) as ServerFrame;
        } catch {
          return;
        }
        if ('event' in frame && (frame.event === 'ping' || frame.event === 'hello')) return;
        // Any inbox data frame → refresh the inbox views.
        queueFlush();
        // Proactive ping: ONLY a brand-new needs-you row. The seen/acted UPDATE
        // and the notification lane never reach here, preserving anti-spam.
        if (frame.op === 'INSERT' && frame.category === 'needs_you') {
          onNeedsYouRef.current?.(frame);
        }
      };
      ws.onclose = () => {
        wsRef.current = null;
        if (closedByUs.current) return;
        if (hasConnected.current) setReconnecting(true);
        const delay = Math.min(30_000, 1_000 * 2 ** attemptRef.current);
        attemptRef.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        // No-op — onclose fires next and triggers reconnect.
      };
    };

    closedByUs.current = false;
    hasConnected.current = false;
    setReconnecting(false);
    connect();

    return () => {
      closedByUs.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
      flush();
      try {
        wsRef.current?.close();
      } catch {
        // Already closed.
      }
      wsRef.current = null;
    };
    // orgId is in deps so a switch re-runs (invalidation targets inboxKeys.all,
    // which covers every org, but re-subscribing keeps the closure honest).
  }, [enabled, orgId, overrideUrl, Ctor, qc]);

  return { reconnecting };
}
